/**
 * Stateless box-score body — usable inside a route page or inside a Modal.
 * Renders line score (clickable to inning modal) + per-side batting tables.
 * Parent owns the snapshot.
 */
import { useState } from 'react';
import InningDetailModal, { type InningModalTarget } from './InningDetailModal';
import LineScore from './LineScore';
import PlayerModal, { type PlayerSeed } from './PlayerModal';
import {
  selectLineScore,
  selectLineup,
  selectPitcherLines,
  selectRibbon,
  type PitchTypeCount,
  type StatcastMap,
} from '../state/selectors';
import { teamColor } from '../state/teamColors';
import styles from '../pages/SubPages.module.css';

interface BoxScorePanelProps {
  snapshot: Record<string, unknown> | null;
  gamePk?: number;
  statcast?: StatcastMap;
}

/** ".740" style — no leading zero, matching MLB.com strike-percentage display. */
function formatStrikePct(pct: number | null): string {
  if (pct == null) return '—';
  return pct.toFixed(3).replace(/^0/, '');
}

/** Compact "FF 30 · SL 30 · CU 23" mix line, or a placeholder when absent. */
function formatPitchMix(mix: PitchTypeCount[]): string {
  if (mix.length === 0) return '—';
  return mix.map((m) => `${m.type} ${m.count}`).join(' · ');
}

export default function BoxScorePanel({ snapshot, gamePk, statcast }: BoxScorePanelProps): JSX.Element {
  const ribbon = selectRibbon(snapshot);
  const ls = selectLineScore(snapshot);
  const awayLineup = selectLineup(snapshot, 'away');
  const homeLineup = selectLineup(snapshot, 'home');
  const awayPitchers = selectPitcherLines(snapshot, 'away');
  const homePitchers = selectPitcherLines(snapshot, 'home');
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSeed | null>(null);
  const [inningTarget, setInningTarget] = useState<InningModalTarget | null>(null);

  if (!ribbon) {
    return <p data-testid="box-panel-loading">Loading…</p>;
  }

  return (
    <>
      {ls && (
        <div style={{ marginBottom: 'var(--s-5)' }}>
          <LineScore
            state={ls}
            awayTeamCode={ribbon.awayTeamCode}
            homeTeamCode={ribbon.homeTeamCode}
            onInningClick={(inning, half) => setInningTarget({ inning, half })}
          />
        </div>
      )}
      <div className={styles.twoCol}>
        {(['away', 'home'] as const).map((side) => {
          const lineup = side === 'away' ? awayLineup : homeLineup;
          const teamCode = side === 'away' ? ribbon.awayTeamCode : ribbon.homeTeamCode;
          const abbr = side === 'away' ? ribbon.awayAbbr : ribbon.homeAbbr;
          const { primary } = teamColor(teamCode);
          return (
            <section key={side} className={styles.section}>
              <h2
                className={styles.sectionHeading}
                style={{ background: primary }}
                data-testid={`box-heading-${side}`}
              >
                {abbr} batting
              </h2>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={`${styles.th} ${styles.thLeft}`}>Player</th>
                    <th className={styles.th}>Pos</th>
                    <th className={styles.th}>AB·H·BB</th>
                  </tr>
                </thead>
                <tbody>
                  {lineup.map((h) => (
                    <tr key={h.id} data-testid={`box-row-${side}-${h.id}`}>
                      <td className={`${styles.td} ${styles.tdLeft}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedPlayer({
                              id: h.id,
                              name: h.name,
                              jersey: h.jersey,
                              position: h.position,
                            })
                          }
                          className={styles.nameButton}
                        >
                          {h.name}
                        </button>
                      </td>
                      <td className={styles.td}>{h.position ?? ''}</td>
                      <td className={styles.td}>{h.battingSummary ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>
      <div className={styles.twoCol} style={{ marginTop: 'var(--s-5)' }}>
        {(['away', 'home'] as const).map((side) => {
          const pitchers = side === 'away' ? awayPitchers : homePitchers;
          const teamCode = side === 'away' ? ribbon.awayTeamCode : ribbon.homeTeamCode;
          const abbr = side === 'away' ? ribbon.awayAbbr : ribbon.homeAbbr;
          const { primary } = teamColor(teamCode);
          return (
            <section key={side} className={styles.section}>
              <h2
                className={styles.sectionHeading}
                style={{ background: primary }}
                data-testid={`box-pitching-heading-${side}`}
              >
                {abbr} pitching
              </h2>
              {pitchers.length === 0 ? (
                <p className={styles.td} data-testid={`box-pitching-empty-${side}`}>
                  No pitchers yet
                </p>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={`${styles.th} ${styles.thLeft}`}>Pitcher</th>
                      <th className={styles.th}>IP</th>
                      <th className={styles.th}>P</th>
                      <th className={styles.th}>S</th>
                      <th className={styles.th}>B</th>
                      <th className={styles.th}>S%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pitchers.map((p) => (
                      <tr key={p.id} data-testid={`box-pitcher-${side}-${p.id}`}>
                        <td className={`${styles.td} ${styles.tdLeft}`}>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedPlayer({
                                id: p.id,
                                name: p.name,
                                jersey: p.jersey,
                                position: 'P',
                              })
                            }
                            className={styles.nameButton}
                          >
                            {p.name}
                          </button>
                          <div
                            style={{
                              fontSize: 'var(--fs-xs)',
                              color: 'var(--c-ink-50)',
                              marginTop: 'var(--s-1)',
                            }}
                            data-testid={`box-pitcher-mix-${side}-${p.id}`}
                          >
                            {formatPitchMix(p.pitchMix)}
                          </div>
                        </td>
                        <td className={styles.td}>{p.inningsPitched}</td>
                        <td className={styles.td}>{p.pitches}</td>
                        <td className={styles.td}>{p.strikes}</td>
                        <td className={styles.td}>{p.balls}</td>
                        <td className={styles.td}>{formatStrikePct(p.strikePct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>
      <PlayerModal
        gamePk={gamePk}
        playerId={selectedPlayer?.id ?? null}
        seed={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
      <InningDetailModal
        open={inningTarget !== null}
        target={inningTarget}
        snapshot={snapshot}
        statcast={statcast}
        gamePk={gamePk}
        onClose={() => setInningTarget(null)}
        onOpenPlayer={(seed) => {
          // Keep the inning modal mounted; PlayerModal stacks on top.
          setSelectedPlayer(seed);
        }}
      />
    </>
  );
}
